import { useRef } from 'react';
import { FocusScope } from 'react-aria';

import { RouteManager } from '../../RouteManager';
import { ToastManager } from '../../ToastManager';
import { DeviceUtils } from '../../store/devices/DeviceUtils.js';
import { userMeDraftStore } from '../../store/users/UserMeDraftStore.ts';

import { platform } from '../../lib/platform';
import { promptMessage } from '../overlay/DialogPrompt';

import { useApi } from '../../store/SelectedHomeyStore.js';
import { useI18n } from '../../hooks/useI18nFormatters';
import { useContextMenuContext } from '../common/context-menu/ContextMenuContextProvider';
import { useUserMe } from '../../store/users/useUserMe.ts';
import { useDevice } from '../../store/devices/useDevices.ts';
import { useFlowsByCardOwnerUri } from '../../store/flow/useFlows.ts';
import { useAdvancedFlowsByCardOwnerUri } from '../../store/advanced-flow/useAdvancedFlows.ts';
import { useLogsByOwnerUri } from '../../store/insights/useLogs.ts';

import { theme } from '../../theme/theme';

import { ContextMenuContent } from '../common/context-menu/ContextMenuContent';
import { DeveloperMenu } from '../common/context-menu/DeveloperMenu';
import { ZoneListBox } from '../zone/ZoneListBox';

import { iconFavorite } from '../../theme/icons/interface/heart-fill';
import { iconNotFavorite } from '../../theme/icons/interface/heart-outline';
import { iconMouseLeftClick } from '../../theme/icons/interface/mouse-left-click';
import { iconTrashFill } from '../../theme/icons/interface/trash-fill/trash-fill';

export function DeviceContextMenuContent(props) {
  const { i18n } = useI18n();
  const ctx = useContextMenuContext();

  const { api } = useApi();
  const { device } = useDevice({ deviceId: props.deviceId });
  const { userMe } = useUserMe();

  const flows = useFlowsByCardOwnerUri();
  const advancedFlows = useAdvancedFlowsByCardOwnerUri();
  const logs = useLogsByOwnerUri();

  const isFavoriteDevice = DeviceUtils.isFavoriteDevice(device, userMe);

  const pendingRef = useRef({
    favorite: null,
  });

  if (device == null) return null;

  const deviceFlows = flows.byCardOwnerUri?.[`homey:device:${props.deviceId}`] ?? null;
  const deviceAdvancedFlows =
    advancedFlows.byCardOwnerUri?.[`homey:device:${props.deviceId}`] ?? null;
  const deviceLogs = logs.byOwnerUri?.[`homey:device:${props.deviceId}`] ?? null;

  const numberLogs = Object.values(deviceLogs ?? {}).filter((log) => {
    return log.type === 'number';
  });
  const deviceFlowsAndAdvancedFlows = Object.values({ ...deviceFlows, ...deviceAdvancedFlows });

  function handleFavoritePress() {
    (async () => {
      const favoriteName = `**${device.name}**`;

      if (pendingRef.current.favorite === true) {
        ToastManager.add({
          message: 'Request still pending, please wait...',
        });
        return;
      }

      if (isFavoriteDevice) {
        try {
          pendingRef.current.favorite = true;

          const nextUserMe = {
            ...userMe,
            properties: {
              ...userMe.properties,
              favoriteDevices: (userMe.properties.favoriteDevices ?? []).filter(
                (id) => id !== device.id
              ),
            },
          };
          userMeDraftStore.setUserMeDraft({ userMe: nextUserMe });

          await new Promise((resolve) => {
            setTimeout(resolve, 1000); // Simulate a delay for the draft update
          });

          await api.users.updateUserMeProperties({
            id: 'favoriteDevices',
            value: nextUserMe.properties.favoriteDevices,
          });

          ToastManager.add({
            icon: iconNotFavorite,
            iconColor: theme.color.icon_light,
            message: i18n.messageFormatter(`device.removeFavorite`, {
              name: favoriteName,
            }),
          });
          userMeDraftStore.setUserMeDraftDone();
        } catch (error) {
          userMeDraftStore.setUserMeDraftFailed();
          ToastManager.handleError(error);
        } finally {
          pendingRef.current.favorite = false;
        }

        return;
      }

      try {
        pendingRef.current.favorite = true;

        const nextUserMe = {
          ...userMe,
          properties: {
            ...userMe.properties,
            favoriteDevices: [...(userMe.properties.favoriteDevices ?? []), device.id],
          },
        };
        userMeDraftStore.setUserMeDraft({ userMe: nextUserMe });

        await api.users.updateUserMeProperties({
          id: 'favoriteDevices',
          value: nextUserMe.properties.favoriteDevices,
        });

        ToastManager.add({
          icon: iconFavorite,
          iconColor: theme.color.special_favorite,
          message: i18n.messageFormatter(`device.addFavorite`, {
            name: favoriteName,
          }),
        });
        userMeDraftStore.setUserMeDraftDone();
      } catch (error) {
        userMeDraftStore.setUserMeDraftFailed();
        ToastManager.handleError(error);
      } finally {
        pendingRef.current.favorite = false;
      }
    })().catch(ToastManager.handleError);
  }

  async function onAction(key) {
    switch (key) {
      case 'quickaction':
        props.onQuickActionRequest?.();
        break;
      case 'rename':
        props.onRenameRequest();
        break;
      case 'favorite':
        handleFavoritePress();
        break;
      case 'settings':
        RouteManager.toDeviceSettings(device.id);
        break;
      case 'repair':
        RouteManager.pushState({
          pairDialog: {
            type: 'repair',
            driverId: device.driverId,
            deviceId: device.id,
          },
        });

        break;
      case 'insights':
        const keys = numberLogs.map((log) => {
          return log.id;
        });

        RouteManager.toInsights({
          defaultSelectedKeys: keys,
          deviceId: device.id,
        });
        break;
      case 'flows':
        RouteManager.toFlows({
          deviceId: device.id,
        });
        break;
      case 'delete':
        if (device.unpair) {
          RouteManager.pushState({
            pairDialog: {
              type: 'unpair',
              driverId: device.driverId,
              deviceId: device.id,
            },
          });
          return;
        }

        try {
          let message;

          switch (true) {
            case device.flags.includes('zwaveMainController') &&
              device.flags.includes('zigbeeMainController'):
              message = i18n.messageFormatter('device.deleteZigbeeZwaveController');
              break;
            case device.flags.includes('zwaveMainController'):
              message = i18n.messageFormatter('device.deleteZwaveController');
              break;
            case device.flags.includes('zigbeeMainController'):
              message = i18n.messageFormatter('device.deleteZigbeeController');
              break;
            default:
              break;
          }

          await promptMessage({
            message:
              message ??
              i18n.messageFormatter('prompt.delete', {
                name: device.name,
              }),
          });

          try {
            await api.devices.deleteDevice({
              id: device.id,
            });
          } catch (error) {
            ToastManager.handleError(error);
          }
        } catch {}
        break;
      default:
        break;
    }
  }

  async function handleSelectionChange(keys, listState) {
    const [key] = keys;
    const item = listState.collection.getItem(key);
    ctx.onCloseRequest();

    if (item != null) {
      try {
        await api.devices.updateDevice({
          id: device.id,
          device: { zone: key },
        });
      } catch (error) {
        ToastManager.handleError(error);
      }
    }
  }

  if (props.isLocked === true) {
    return (
      <ContextMenuContent.List>
        <ContextMenuContent.ListItem
          label={i18n.messageFormatter(`device.favorite`)}
          icon={isFavoriteDevice ? iconFavorite : iconNotFavorite}
          onPress={() => {
            onAction('favorite');
          }}
        />

        <ContextMenuContent.Divider />

        <ContextMenuContent.SubMenu title={i18n.messageFormatter(`device.moveTo`)}>
          <ZoneListBox selectedKeys={[device.zone]} onSelectionChange={handleSelectionChange} />
        </ContextMenuContent.SubMenu>

        <DeveloperMenu onIdRequest={() => device?.id} onResourceRequest={() => device} />

        <ContextMenuContent.Divider />
        <ContextMenuContent.ListItem
          color={theme.color.danger}
          label={i18n.messageFormatter('common.delete')}
          variant="danger"
          icon={iconTrashFill}
          onPress={() => {
            onAction('delete');
          }}
        />
      </ContextMenuContent.List>
    );
  }

  return (
    <ContextMenuContent.List>
      <ContextMenuContent.ListItem
        label={props.quickActionTitle}
        hint={`${platform.optionSymbol} +`}
        icon={iconMouseLeftClick}
        isDisabled={device.ui?.quickAction == null}
        onPress={() => {
          onAction('quickaction');
        }}
      />

      <ContextMenuContent.ListItem
        label={i18n.messageFormatter(`device.favorite`)}
        icon={isFavoriteDevice ? iconFavorite : iconNotFavorite}
        onPress={() => {
          onAction('favorite');
        }}
      />

      <ContextMenuContent.Divider />

      <ContextMenuContent.ListItem
        hint={numberLogs.length}
        label={i18n.messageFormatter(`navigation.insights`)}
        onPress={() => {
          onAction('insights');
        }}
      />

      <ContextMenuContent.ListItem
        hint={deviceFlowsAndAdvancedFlows.length}
        label="Flows"
        onPress={() => {
          onAction('flows');
        }}
      />

      <ContextMenuContent.Divider />

      <ContextMenuContent.SubMenu title={i18n.messageFormatter(`device.moveTo`)}>
        <FocusScope autoFocus={true} contain restoreFocus={false}>
          <ZoneListBox selectedKeys={[device.zone]} onSelectionChange={handleSelectionChange} />
        </FocusScope>
      </ContextMenuContent.SubMenu>

      <ContextMenuContent.ListItem
        label={i18n.messageFormatter('common.rename')}
        onPress={() => {
          onAction('rename');
        }}
      />

      {device.repair && (
        <ContextMenuContent.ListItem
          label={i18n.messageFormatter(`device.repair`)}
          onPress={() => {
            onAction('repair');
          }}
        />
      )}

      <ContextMenuContent.ListItem
        label={i18n.messageFormatter(`device.settings.title`)}
        onPress={() => {
          onAction('settings');
        }}
      />

      <DeveloperMenu onIdRequest={() => device?.id} onResourceRequest={() => device} />

      <ContextMenuContent.Divider />
      <ContextMenuContent.ListItem
        color={theme.color.danger}
        label={i18n.messageFormatter('common.delete')}
        variant="danger"
        icon={iconTrashFill}
        onPress={() => {
          onAction('delete');
        }}
      />
    </ContextMenuContent.List>
  );
}
